import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import Badge from '../components/Badge';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function BiofeedbackSession() {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale'); // 'inhale' or 'exhale'
  const { isFirstVisit, setVisited } = useFirstVisit('biofeedback');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setPhase(prev => {
        if (prev === 'inhale') {
          setPhase('exhale');
          return 'exhale';
        } else {
          setPhase('inhale');
          return 'inhale';
        }
      });
    }, phase === 'inhale' ? 4000 : 6000); // 4s inhale, 6s exhale

    return () => clearInterval(interval);
  }, [isActive, phase]);

  function toggleSession() {
    setIsActive(!isActive);
    if (!isActive) {
      setPhase('inhale');
    }
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title="🧘 Breathing Exercise" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="biofeedback" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <Card style={{ textAlign: 'center' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '700' }}>🧘 Breathing Exercise</h1>
        <p style={{ color: '#64748b', margin: '0 0 24px 0' }}>
          Follow the circle: breathe in as it grows, breathe out as it shrinks
        </p>
      </Card>

      <Card style={{ textAlign: 'center', padding: '40px' }}>
        <div 
          className={isActive ? (phase === 'inhale' ? 'breathe-in' : 'breathe-out') : ''}
          style={{
            width: '180px',
            height: '180px',
            backgroundColor: isActive ? (phase === 'inhale' ? '#4ade80' : '#4361ee') : '#cbd5e1',
            borderRadius: '50%',
            margin: '0 auto 24px auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isActive ? '0 0 30px rgba(67, 97, 238, 0.3)' : 'none'
          }}
        >
          <div style={{ 
            fontSize: '48px', 
            color: 'white',
            fontWeight: 'bold'
          }}>
            {isActive ? (phase === 'inhale' ? 'IN' : 'OUT') : 'START'}
          </div>
        </div>

        <h2 style={{ 
          color: '#2c3e50', 
          margin: '16px 0',
          fontSize: '24px',
          opacity: isActive ? 1 : 0.7,
          fontWeight: '600'
        }}>
          {!isActive ? 'Ready to begin?' : (phase === 'inhale' ? 'Breathe In...' : 'Breathe Out...')}
        </h2>

        <Button 
          onClick={toggleSession}
          variant={isActive ? "danger" : "success"}
          size="lg"
          style={{ 
            fontSize: '18px',
            padding: '16px 32px',
            fontWeight: '600',
            marginTop: '16px'
          }}
        >
          {isActive ? 'Stop Session' : 'Start Breathing'}
        </Button>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ color: '#2c3e50', margin: 0, fontWeight: '600' }}>📊 Session Insights</h3>
          <Badge variant="primary">Live</Badge>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <span style={{ color: '#64748b', fontSize: '14px' }}>Heart Rate Variability</span>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#4ade80' }}>Improving</div>
          </div>
          <div>
            <span style={{ color: '#64748b', fontSize: '14px' }}>Stress Level</span>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#4361ee' }}>Moderate</div>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#f1f5f9', padding: '16px', borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>Sessions Today</span>
            <span style={{ fontWeight: '600' }}>2/3</span>
          </div>
          <div style={{ 
            height: '8px', 
            backgroundColor: '#e2e8f0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div 
              style={{ 
                height: '100%', 
                width: '66%', 
                backgroundColor: '#4361ee',
                borderRadius: '4px'
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

export default BiofeedbackSession;
